/**
 * Pagination — React (web) implementation.
 *
 * Uses CSS transitions for smooth dot animations.
 * All styling references shared constants in Pagination.ts,
 * which in turn reference design system elements.
 * No raw hex values or pixel sizes in this file.
 */

import React from 'react';
import type { PaginationProps } from './Pagination';
import {
  DOT_SIZE,
  DOT_WIDTH_ACTIVE,
  DOT_GAP,
  DOT_RADIUS,
  DOT_COLOR_INACTIVE,
  DOT_COLOR_ACTIVE,
  DOT_TRANSITION_DURATION,
  CONTAINER_BACKGROUND,
  CONTAINER_RADIUS,
  CONTAINER_PADDING_VERTICAL,
  CONTAINER_PADDING_HORIZONTAL,
} from './Pagination';

export interface WebPaginationProps extends PaginationProps {
  'aria-label'?: string;
  /** Hide from assistive tech when a wrapper carries the semantics (e.g. a
   *  progressbar wrapping the dots as its visual). */
  'aria-hidden'?: boolean;
  'data-testid'?: string;
}

function Dot({ active }: { active: boolean }) {
  const style: React.CSSProperties = {
    flexShrink: 0,
    width: active ? DOT_WIDTH_ACTIVE : DOT_SIZE,
    height: DOT_SIZE,
    borderRadius: DOT_RADIUS,
    backgroundColor: active ? DOT_COLOR_ACTIVE : DOT_COLOR_INACTIVE,
    transition: `all ${DOT_TRANSITION_DURATION}ms ease-in-out`,
  };

  return <span style={style} aria-hidden="true" />;
}

export function Pagination({
  totalPages,
  currentPage,
  'aria-label': ariaLabel,
  'aria-hidden': ariaHidden,
  'data-testid': dataTestId,
}: WebPaginationProps) {
  if (totalPages < 1) {
    return null;
  }

  const label = ariaLabel ?? `Page ${currentPage} of ${totalPages}`;

  const containerStyle: React.CSSProperties = {
    display: 'inline-flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: DOT_GAP,
    backgroundColor: CONTAINER_BACKGROUND,
    borderRadius: CONTAINER_RADIUS,
    paddingTop: CONTAINER_PADDING_VERTICAL,
    paddingBottom: CONTAINER_PADDING_VERTICAL,
    paddingLeft: CONTAINER_PADDING_HORIZONTAL,
    paddingRight: CONTAINER_PADDING_HORIZONTAL,
  };

  return (
    <div
      role="img"
      style={containerStyle}
      aria-label={label}
      aria-hidden={ariaHidden || undefined}
      data-testid={dataTestId}
    >
      {Array.from({ length: totalPages }, (_, i) => (
        <Dot key={i} active={i === currentPage - 1} />
      ))}
    </div>
  );
}
